import { useEffect } from "react";
import { createFileRoute, Outlet, redirect } from "@tanstack/react-router";
import z from "zod";

import { CareplanContext } from "@/domains/careplan/contexts/use-careplan-context";
import { useCareplan } from "@/domains/careplan/queries/use-careplan";
import { SUBSCRIPTION_PROCESSING_TIMEOUT_MS } from "@/domains/subscriptions/models/constants";
import { getCartStoreState, getSessionStoreState, isCartProcessing, useCartStore } from "@/stores";
import { unlockPageScroll } from "@/utils";
import { CareplanCart } from "./-components/cart/careplan-cart";
import { NavBar, Sidebar } from "./-components";

const privateLayoutUrlSchema = z.object({
  discount: z.string().optional(),
  subscription_processing: z.boolean().optional(),
});

const PrivateLayout = () => {
  const { closeCart, showCart, toggleCart } = useCartStore();
  const { careplan } = useCareplan();

  useEffect(() => {
    return () => {
      unlockPageScroll();
    };
  }, []);

  return (
    <div className="flex min-h-dvh flex-col">
      <NavBar onToggleCart={toggleCart} showCart={!!careplan} />

      <Sidebar onClose={closeCart} show={showCart}>
        {careplan ? (
          <CareplanContext.Provider value={careplan}>
            <CareplanCart onClose={closeCart} />
          </CareplanContext.Provider>
        ) : null}
      </Sidebar>

      {/*
        The skip link's single target for every authenticated route. tabIndex
        moves focus here, not just the scroll position.
      */}
      <div className="flex min-h-0 flex-1 flex-col" id="main-content" tabIndex={-1}>
        <Outlet />
      </div>
    </div>
  );
};

export const Route = createFileRoute("/_private")({
  beforeLoad: ({ location, search }) => {
    const { session } = getSessionStoreState();

    if (!session?.token) {
      throw redirect({ to: "/login", search: { redirect: location.href } });
    }

    const { openCart, setSubscriptionProcessingDeadline } = getCartStoreState();
    const alreadyTracking = isCartProcessing(getCartStoreState());

    if (search.subscription_processing) {
      if (!alreadyTracking) {
        setSubscriptionProcessingDeadline(Date.now() + SUBSCRIPTION_PROCESSING_TIMEOUT_MS);
      }
      openCart();
    }
  },
  component: PrivateLayout,
  validateSearch: privateLayoutUrlSchema,
});
